<template>
  <div
    class="c-chat__dialog"
    :class="{ opened: chatStore.chatOpened }">
    <div
      ref="dialogContentRef"
      class="c-chat__dialogContent"
      :class="{ '-hasThreadOpened': chatStore.chatThread }">
      <div class="c-chat__dialogSidebar">
        <div class="c-chat__dialogSidebarHeader">
          <div class="o-flex -alignCenter -spaceBetween -gap2 -fullWidth">
            <div
              class="c-chat__dialogSidebarHeaderInputWrapper o-flex__item -grow"
              :class="{ '-focused': isUsersSearchQueryInputFocused, '-hasValue': !!chatStore.usersSearchQuery }"
              @click="focusUserSearchInput"
              @keydown.esc="closeShowUsersResult">
              <button
                id="users-search-icon"
                class="o-flex c-button -icon -small -flat c-chat__searchIcon"
                @click="focusUserSearchInput">
                <SvgIcon
                  type="chat_search"
                  small />
              </button>
              <div class="o-flex__item -grow s-position-relative">
                <input
                  ref="usersSearchQueryRef"
                  v-model="chatStore.usersSearchQuery"
                  type="text"
                  class="c-chat__dialogSidebarHeaderInput"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-dashlane-ignore="true"
                  data-form-type="other"
                  @focus="openShowUsersResult"
                  @blur="closeShowUsersResult" />
                <label
                  for="usersSearchQueryRef"
                  class="c-chat__dialogSidebarHeaderInputLabel t-body3">
                  {{$t('chat.search_users')}}
                </label>
              </div>
            </div>
            <button
              class="c-chat_closeBtn c-button -flat -transparent u-hide@tabletLandscape"
              @click="closeChatDialog">
              <SvgIcon
                medium
                type="close" />
            </button>
          </div>
          <div
            v-if="!chatStore.usersList.length && !isUsersSearchQueryInputFocused"
            class="c-chat__dialogSidebarHeaderBubble">
            <div class="t-body3 u-colorDark u-marginBottom2">{{$t('chat.you_do_not_have_any_messages_yet')}}</div>
            <div class="t-caption1 u-colorDarkMediumEmphasis">{{$t('chat.look_and_select_person_to_message')}}</div>
          </div>
          <UsersSearchResult
            :show="showUsersSearchResult"
            @openUserThread="openUserThread" />
        </div>
        <div class="c-chat__dialogSidebarContent">
          <template v-if="chatStore.usersListLoaded">
            <template v-for="user in chatStore.usersList">
              <UsersListItem
                :key="user.identity"
                :message="user.lastMessage"
                :has-unread-messages="chatStore.threadsHasUnreadMessages[user.azure_chat_thread_id]"
                :user="user"
                @click="openUserThread(user)" />
            </template>
          </template>
          <template v-else>
            <div
              class="o-flex -alignCenter -center u-marginTop2">
              <LoadingSpinner />
            </div>
          </template>
        </div>
      </div>
      <div class="c-chat__dialogMain">
        <div class="c-chat__dialogMainHeader o-flex -alignCenter -spaceBetween">
          <div class="o-flex -alignCenter -gap4">
            <div v-if="chatStore.userDetails">
              <button
                class="c-chat__dialogBackButton"
                @click="backToChatThread">
                <SvgIcon
                  tiny
                  type="chat_arrow_left" />
              </button>
            </div>
            <div
              v-if="!chatStore.userDetails"
              :class="{ 'u-hide@tabletMiniDown': isChatSearchQueryInputFocused }"
              class="u-hide@tabletLandscape">
              <button
                class="c-chat__dialogBackButton"
                @click="backToUsersList">
                <SvgIcon
                  tiny
                  type="chat_arrow_left" />
              </button>
            </div>
            <button
              v-if="!chatStore.userDetails && chatStore.chatThread"
              class="c-chat__dialogMainHeaderUser c-button -flat o-flex -alignCenter -gap2 s-p0"
              :class="{ 'u-hide@tabletMiniDown': isChatSearchQueryInputFocused }"
              @click="openUserDetails">
              <UserAvatar
                :user="chatStore.chatThread.participant"
                small />
              <span
                class="c-chat__dialogMainHeaderUserName t-body3 t-overflowEllipsis t-noWrap"
                v-text="chatStore.chatThread.participant.displayName" />
            </button>
          </div>

          <div
            ref="chatSearchQueryWrapperRef"
            class="c-chat__dialogMainSearchWrapper o-flex -spaceBetween -alignCenter -gap3 o-flex__item">
            <div class="o-flex -alignCenter -spaceBetween -gap2 o-flex__item">
              <button
                v-if="chatSearchInputFocused"
                class="c-button -flat s-p0"
                @click="handleChatSearchBlur">{{
                  $t('chat.cancel') }}
              </button>
              <div
                v-if="chatStore.chatThread"
                class="c-chat__dialogSidebarHeaderInputWrapper -circle@tabletMiniDown -plain"
                :class="{ '-focused': chatSearchInputFocused }"
                @click="focusChatSearchInput"
                @keydown.esc="handleChatSearchBlur">
                <button
                  class="o-flex c-button -icon -small -flat c-chat__searchIcon"
                  @click="focusChatSearchInput">
                  <SvgIcon
                    small
                    type="chat_search" />
                </button>
                <input
                  id="chat-search-input"
                  ref="chatSearchQueryRef"
                  v-model="chatStore.chatSearchQuery"
                  type="search"
                  :placeholder="$t('chat.search_chat')"
                  class="c-chat__dialogSidebarHeaderInput"
                  @input="searchChatMessages"
                  @focus="handleChatSearchFocus"
                  @blur="handleChatSearchBlur" />
              </div>
            </div>
            <button
              class="c-chat_closeBtn c-button -flat -transparent u-marginRight1"
              @click="closeChatDialog">
              <SvgIcon
                medium
                type="close" />
            </button>
          </div>
        </div>
        <div
          v-if="chatStore.usersListLoaded"
          class="c-chat__dialogMainContent">
          <template v-if="!chatStore.userDetails">
            <template v-if="!chatStore.chatSearchQuery">
              <div class="c-chat__dialogMainContentFlexWrapper">
                <div
                  key="content-wrapper"
                  class="c-chat__dialogMainContentWrapper"
                  @dragover.prevent="dragOver"
                  @dragleave.prevent="dragLeave"
                  @drop.prevent="dropFile">
                  <ChatWelcome v-if="!chatStore.chatThread && !chatStore.usersList.length" />
                  <MessagesContainer
                    v-if="chatThreadLoaded"
                    ref="messagesContainerRef"
                    :chat-thread="chatStore.chatThread"
                    @chatMessageReceived="resetTypingState" />
                  <LastMessagesButton v-if="chatThreadStore?.unSeenMessages && chatStore.hasNewUnseenMessages && !chatThreadStore?.isScrollerAtBottom" />
                  <transition name="fade">
                    <ChatFileUploadContainer
                      v-if="chatStore.chatThread"
                      v-show="chatStore.isDragging" />
                  </transition>
                  <div
                    v-show="!!chatThreadStore?.typingIndicator"
                    class="c-chat__isTyping t-body3 u-marginLeft1">
                    {{ getParticipantDisplayName(chatStore.chatThread) }} {{ $t('chat.is_typing') }}...
                  </div>
                </div>
                <ChatMessageActions
                  v-if="chatStore.chatThread && chatStore.fileUploadRef"
                  key="message-actions" />
              </div>
            </template>
            <template v-else>
              <div class="c-chat__dialogMainContentWrapper">
                <SearchMessagesHistory :chat-thread="chatStore.chatThread" />
              </div>
            </template>
          </template>
          <template v-else-if="chatStore.userDetails">
            <Details key="user-details" />
          </template>
        </div>
      </div>
    </div>
    <portal
      v-if="chatStore?.isImageModalOpen && chatStore?.imageToShowUrl"
      to="modal">
      <ImageModal
        v-model="chatStore.isImageModalOpen"
        :url="chatStore.imageToShowUrl" />
    </portal>
  </div>
</template>

<script setup>
import {
  nextTick, onMounted, onUnmounted, ref, watch,
} from 'vue'
import { useRoute } from 'vue-router/composables'

import ImageModal from '@/components/ImageModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import api from '@/logic/api'
import { loadEmojiData } from '@/logic/emoji/emojiLoader'
import helpers from '@/logic/helpers'
import { useChatSearchMessagesHistoryStore } from '@/modules/chat/chatSearchMessagesHistoryStore'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import ChatFileUploadContainer from '@/modules/chat/components/ChatFileUploadContainer.vue'
import ChatMessageActions from '@/modules/chat/components/ChatMessageActions.vue'
import ChatWelcome from '@/modules/chat/components/ChatWelcome.vue'
import UserAvatar from '@/modules/chat/components/DialogSidebar/UserAvatar.vue'
import UsersListItem from '@/modules/chat/components/DialogSidebar/UsersListItem.vue'
import UsersSearchResult from '@/modules/chat/components/DialogSidebar/UsersSearchResult.vue'
import LastMessagesButton from '@/modules/chat/components/LastMessagesButton.vue'
import MessagesContainer from '@/modules/chat/components/MessagesContainer.vue'
import SearchMessagesHistory from '@/modules/chat/components/SearchMessagesHistory.vue'
import Details from '@/modules/chat/components/UserProfile/Details.vue'
import { getParticipantDisplayName } from '@/modules/chat/logic/chatThread'
import { joinGroup } from '@/modules/global/azureWebPubSub'

const chatStore = useChatStore()
const chatSearchMessagesHistoryStore = useChatSearchMessagesHistoryStore()

const isUsersSearchQueryInputFocused = ref(false)
const isChatSearchQueryInputFocused = ref(false)
const showUsersSearchResult = ref(false)
const usersSearchQueryRef = ref(null)
const chatSearchQueryRef = ref(null)
const chatSearchQueryWrapperRef = ref(null)
const chatSearchInputFocused = ref(false)
const dialogContentRef = ref(null)
// This variable to indicate the the chatthread is loaded,
// but we need it on mobile to have a small delay while sliding back to threds list
const chatThreadLoaded = ref(false)
const typingState = ref(null)
const messagesContainerRef = ref(null)
const timeouts = ref(new Set())

const chatThreadStore = ref(null)
let lastThreadId = null

const handleChatSearchFocus = () => {
  chatSearchInputFocused.value = true
  isChatSearchQueryInputFocused.value = true
  chatSearchQueryWrapperRef.value?.classList.add('-grow')
  chatSearchQueryWrapperRef.value?.querySelector('.o-flex').classList.add('-grow')
}

const handleChatSearchBlur = () => {
  chatSearchInputFocused.value = false
  isChatSearchQueryInputFocused.value = false
  chatSearchQueryWrapperRef.value?.classList.remove('-grow')
  chatSearchQueryWrapperRef.value?.querySelector('.o-flex').classList.remove('-grow')
}

const openShowUsersResult = () => {
  showUsersSearchResult.value = true
  isUsersSearchQueryInputFocused.value = true
}

const closeShowUsersResult = ($event) => {
  const timeoutId = setTimeout(() => {
    nextTick().then(() => {
      // To prevent blinking user search component when user clicks on search icon
      if ($event.relatedTarget?.id === 'users-search-icon') {
        return
      }
      showUsersSearchResult.value = false
      isUsersSearchQueryInputFocused.value = false
    })
  }, 200)
  timeouts.value.add(timeoutId)
}

const focusUserSearchInput = () => {
  isUsersSearchQueryInputFocused.value = true
  nextTick().then(() => usersSearchQueryRef.value?.focus())
}

const focusChatSearchInput = () => {
  handleChatSearchFocus()
  nextTick().then(() => chatSearchQueryRef.value?.focus())
}

const closeChatDialog = () => {
  chatStore.chatOpened = false
}

const openUserThread = async (user) => {
  chatStore.chatSearchQuery = null
  chatStore.userDetails = null
  if (chatStore.chatThread?.id === user.threadId) {
    await nextTick()
    chatStore.updateMessagesContainerHeight()
    chatThreadStore.value.chatThreadIsOpening = false
    chatThreadLoaded.value = true
    return
  }

  chatStore.chatSearchQuery = null
  chatStore.userDetails = null
  chatStore.chatThread = null
  chatThreadLoaded.value = false
  await nextTick()
  chatStore.chatThread = chatStore.myChatThreads.find((myChatThread) => myChatThread.id === user.threadId)
  chatThreadLoaded.value = true
  await nextTick()
  chatStore.updateMessagesContainerHeight()
}

const openUserDetails = () => {
  chatStore.userDetails = chatStore.chatThread.participant
}

const backToChatThread = async () => {
  await openUserThread(chatStore.chatThread.participant)
}

const backToUsersList = () => {
  chatStore.userDetails = null
  chatStore.chatSearchQuery = null

  const el = dialogContentRef.value
  if (helpers.isMobileViewport() && el) {
    // To be synced with css transition time,
    // whenever we change the transition time in css, we don't need to change it here.
    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'background-color') {
        return
      }
      chatThreadLoaded.value = false
    }
    el.addEventListener('transitionend', onTransitionEnd, { once: true })
  } else {
    chatThreadLoaded.value = false
  }

  chatStore.chatThread = null
}

function resetTypingState() {
  typingState.value = null
}

const dragOver = () => {
  chatStore.isDragging = true
}

const dragLeave = () => {
  chatStore.isDragging = false
}

const dropFile = async (event) => {
  event.preventDefault()
  dragLeave()
  if (event.dataTransfer.files.length > 1) {
    api.store.commit('notifications/addError', {
      title: api.i18n.t('notifications.max_file_count', { count: 1 }),
    })
    return
  }
  if (event.target === document.querySelector('.c-chat__fileUploadWrapper')) {
    await chatStore.fileUploadRef?.getFilePond().addFile(event.dataTransfer.files[0])
    await chatStore.fileUploadRef?.getFilePond().processFile()
  }
}

watch(() => chatStore.chatThread, (newThread) => {
  if (newThread) {
    if (newThread.azure_chat_thread_id !== lastThreadId) {
      chatThreadStore.value = useChatThreadStore(newThread.azure_chat_thread_id)

      if (chatThreadStore.value.messages.length > 0) {
        chatThreadStore.value.chatThreadIsOpening = false
      }
      lastThreadId = newThread.azure_chat_thread_id
      chatThreadStore.value.unSeenMessages = 0
      chatThreadStore.value.isScrollerAtBottom = true
    }
  }
})

watch(() => chatStore.chatSearchQuery, async () => {
  chatStore.userDetails = null
  await nextTick()
  chatStore.updateMessagesContainerHeight()
})

const searchChatMessages = helpers.debounce(async () => {
  if (!chatStore.chatSearchQuery) {
    chatThreadStore.value.chatThreadIsOpening = false
  }
  if (!chatStore.chatThread) {
    return
  }
  await chatSearchMessagesHistoryStore.searchMessages(chatStore.chatThread.id, chatStore.chatSearchQuery)
}, 500)

const initializeChatThreads = async () => {
  chatStore.usersListLoaded = true
  if (helpers.isMobileViewport()) {
    chatStore.chatThread = null
    chatStore.userDetails = null
  } else if (chatStore.usersList.length && !chatStore.chatThread) {
    await openUserThread(chatStore.usersList[0])
  }
  await chatStore.updateHasUnreadMessagesInThreads()
}

const initializeCurrentThread = () => {
  if (!chatStore.chatThread) return

  chatThreadLoaded.value = true
  // To prevent having white screen when opening chat dialog if chat thread is already selected before
  setTimeout(() => {
    if (!chatThreadStore.value) {
      chatThreadStore.value = useChatThreadStore(chatStore.chatThread.azure_chat_thread_id)
    }
    chatThreadStore.value.chatThreadIsOpening = false
    chatStore.updateMessagesContainerHeight()
    chatThreadStore.value.scrollContainerDown()
  }, 100)
}

const subscribeToPresenceGroups = async () => {
  await Promise.all(chatStore.myChatThreads.map(async (thread) => {
    if (!chatStore.subscribedGroups[`user-online-${thread.participant.user_id}`]) {
      chatStore.subscribedGroups[`user-online-${thread.participant.user_id}`] = await joinGroup(`user-online-${thread.participant.user_id}`, async (data) => {
        if (thread.participant.user_id === data.user_id) {
          thread.participant.isOnline = data.isOnline
        }
      })
    }
  }))
}
const initializeChat = async () => {
  await initializeChatThreads()
  initializeCurrentThread()
  await subscribeToPresenceGroups()
}

const debouncedSearch = helpers.debounce(() => {
  chatStore.searchUsers()
}, 0, true)

watch(() => chatStore.usersSearchQuery, () => {
  debouncedSearch()
})

function lockScroll() {
  const html = document.documentElement
  const body = document.body
  if (html) {
    html.style.overflow = 'hidden'
    html.style.height = '100%'
  }
  if (body) {
    body.style.overflow = 'hidden'
    body.style.height = '100%'
  }
}

function unlockScroll() {
  const html = document.documentElement
  const body = document.body
  if (html) {
    html.style.overflow = ''
    html.style.height = ''
  }
  if (body) {
    body.style.overflow = ''
    body.style.height = ''
  }
}

function updateScrollLock() {
  if (helpers.isMobileViewport() && chatStore.chatOpened) {
    lockScroll()
  } else {
    unlockScroll()
  }
}

const handleZoom = helpers.debounce(() => {
  chatStore.updateHasUnreadMessagesInThreads()
}, 200)

// React to chat open state changes
watch(() => chatStore.chatOpened, () => {
  updateScrollLock()
}, { immediate: true })

const handleEscKeydown = (event) => {
  if (event.key === 'Escape' && chatStore.chatOpened) {
    chatStore.chatOpened = false
  }
}

onMounted(() => {
  initializeChat()
  // Warm emoji data cache so EmojiPicker opens instantly
  loadEmojiData()
  window.addEventListener('resize', chatStore.updateMessagesContainerHeight)
  window.addEventListener('resize', updateScrollLock)

  // To handle when zoom in/out, so the messages container still fit chat area and not being smaller
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleZoom)
  }
  window.addEventListener('keydown', handleEscKeydown)
})

onUnmounted(() => {
  Object.values(chatStore.subscribedGroups).forEach((disconnect) => {
    disconnect()
  })
  timeouts.value.forEach((timeoutId) => {
    clearTimeout(timeoutId)
  })
  timeouts.value.clear()
  chatStore.usersListLoaded = false
  window.removeEventListener('resize', chatStore.updateMessagesContainerHeight)
  window.removeEventListener('resize', updateScrollLock)

  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', handleZoom)
  }
  window.removeEventListener('keydown', handleEscKeydown)
  // Ensure scroll is unlocked when chat dialog is destroyed
  unlockScroll()
})

// Auto-close chat on mobile when navigating to another page
const route = useRoute()

watch(() => route.fullPath, () => {
  chatStore.closeChatWhenNavigatingInMobile()
})

</script>

<style scoped lang="scss">
.s-p0 {
  padding: 0;
}

.s-position-relative {
  position: relative;
}
</style>
